/**
 * LA PESTAÑA SUPLEMENTACIÓN DE LA FICHA, COMO LA DE CALMA (Jesús, 20-10).
 *
 * Dos columnas: a la izquierda los «descartados» (el catálogo menos lo que ya lleva el
 * cliente, con un filtro de texto) y a la derecha lo que lleva. Aquí vive la lógica pura de
 * esa pantalla; quién guarda y cómo sigue siendo cosa de la ficha y del servidor
 * (routes/supplements.py), que no cambian.
 */
import { matchAll, palabrasDe } from './busquedaAlimentos';
import { esUnDia, hoyEnEspana, lunesSiguiente } from './horaEspana';

/**
 * ¿Son el mismo suplemento? Por su ficha del catálogo si los dos la traen; si no, por el
 * título (los escritos a mano no llevan `catalog_id`). Una ficha del catálogo se pasa como
 * viene (`id`): se lee como su `catalog_id`.
 */
export const mismoSup = (a, b) => {
    const idA = a?.catalog_id || a?.id;
    const idB = b?.catalog_id || b?.id;
    if (idA && idB) return idA === idB;
    return (a?.titulo || '').trim().toLowerCase() === (b?.titulo || '').trim().toLowerCase();
};

/**
 * Las fichas del catálogo que casan con lo escrito, por orden alfabético. El filtro es el de
 * Calma, el mismo que el buscador de alimentos: tienen que estar TODAS las palabras, sin mirar
 * tildes ni mayúsculas. Lo usan los «descartados» y el «Ver catálogo» de la pestaña (20-10).
 */
export const buscarEnElCatalogo = (catalogo, filtro = '') => {
    const palabras = palabrasDe(filtro);
    return (catalogo || [])
        .filter(c => !palabras.length || matchAll(c.titulo || '', palabras))
        .sort((x, y) => (x.titulo || '').localeCompare(y.titulo || '', 'es'));
};

/**
 * Los «Suplementos descartados»: las fichas del catálogo que el cliente NO lleva en `lista`,
 * filtradas por lo escrito como en `buscarEnElCatalogo`.
 */
export const descartados = (catalogo, lista, filtro = '') => buscarEnElCatalogo(
    (catalogo || []).filter(c => !(lista || []).some(it => mismoSup(c, it))), filtro);

/**
 * La fecha con la que se abre la próxima versión: la que ya tiene preparada la app
 * (`siguiente_fecha`) y, si no hay, el lunes que viene según el reloj de quien la pone.
 */
export const fechaDeLaSiguiente = (siguienteFecha, hoy = hoyEnEspana()) =>
    (esUnDia(siguienteFecha) ? siguienteFecha : lunesSiguiente(hoy));

/**
 * La fecha que dice el botón «Guardar suplementos para…» según el bloque que se edita.
 * El actual sin fecha empieza hoy (es lo que hace el servidor si no se le dice nada).
 */
export const fechaDelGuardado = (bloque, protocolo, hoy = hoyEnEspana()) => (bloque === 'actual'
    ? (protocolo?.actual_fecha || hoy)
    : fechaDeLaSiguiente(protocolo?.siguiente_fecha, hoy));

/**
 * Las plantillas de «Nota para el cliente», copiadas tal cual de Calma
 * (_calma_ref/group-stores, la lista que usa FormularioSuplementacionConCorreo). El botón
 * pone el texto en la nota, en lugar de lo que hubiera, igual que allí.
 */
export const PLANTILLAS_DE_NOTA = [
    {
        id: 'hydropeptides-colectiva',
        titulo: 'Hydropeptides/MAP en clase colectiva',
        texto: 'Si das una clase colectiva a una intensidad elevada: 15 g de Hydropeptides o MAP + 300 ml de Aquarius (del normal, no zero). Lo diluyes en otros 300 ml de agua mineral y te lo tomas durante la sesión. Esto lo agregas a la dieta del día como extra, no lo cuentas (es decir, no lo restas de tus macros asignados).',
    },
    {
        id: 'terapia-ergogenica',
        titulo: 'Ver terapia ergogénica',
        texto: 'En el documento de tu terapia ergogénica tienes suplementación adicional.',
    },
];
